import { clubCoversAll, clubCoversAny, estimatedBuildMinutes, solutionFacts, } from "../utils/solutionFacts";
export const stackKey = (entry) => `${entry.player.resourceId}:${entry.source}:${entry.untradeable === true}`;
export const stackEntries = (entries) => entries.reduce((stacks, entry) => {
    const key = stackKey(entry);
    const last = stacks[stacks.length - 1];
    if (last && last.key === key)
        last.entries.push(entry);
    else
        stacks.push({ key, entries: [entry] });
    return stacks;
}, []);
export const isMarked = (marks, entryId) => marks[entryId] === true;
export const bracketEntryIds = (bracket) => bracket.players.map((entry) => entry.entryId);
/** Points the whole row is worth once every card in it is completed. */
export const bracketSubtotal = (bracket) => bracket.points * bracket.players.length;
export const isBracketDone = (bracket, marks) => bracket.players.length > 0 &&
    bracket.players.every((entry) => isMarked(marks, entry.entryId));
/**
 * Whether toggling `entryIds` would leave every card in the row marked — what
 * turns the last card of a row into "the row is finished", so the screen can
 * fold it as the toggle lands rather than a render later.
 */
export const togglingFinishesBracket = (bracket, marks, entryIds) => bracket.players.every((entry) => isMarked(marks, entry.entryId) !== entryIds.includes(entry.entryId));
const solutionEntries = (solution) => solution.brackets.flatMap((bracket) => bracket.players);
export const pointsEarned = (solution, marks) => solutionEntries(solution).reduce((sum, entry) => (isMarked(marks, entry.entryId) ? sum + entry.points : sum), 0);
/** 0..1, clamped — a solution can overshoot the points target. */
export const progressRatio = (earned, target) => {
    if (target <= 0)
        return 0;
    return Math.min(1, Math.max(0, earned / target));
};
/**
 * Coins still to spend: market cards the user has not marked yet. Club cards
 * never count, marked or not — they cost nothing.
 */
export const costRemaining = (solution, marks) => solutionEntries(solution).reduce((sum, entry) => entry.source === "MARKET" && !isMarked(marks, entry.entryId)
    ? sum + entry.price
    : sum, 0);
/** Coins already spent on marked market cards. */
export const costSpent = (solution, marks) => solutionEntries(solution).reduce((sum, entry) => entry.source === "MARKET" && isMarked(marks, entry.entryId)
    ? sum + entry.price
    : sum, 0);
/** What the club players in this solution save against buying them all. */
export const clubSaving = (solution) => solution.totalValue - solution.transferCost;
export const isSolutionDone = (solution, marks) => solution.brackets.every((bracket) => isBracketDone(bracket, marks));
/**
 * Whether the club covers any of the squad at all. Thin wrappers over the
 * shared rules so a Streamline caller can hand over a solution rather than
 * pulling the two counts out itself.
 */
export const isAnyFromClub = (solution) => clubCoversAny(solution.ownedCount);
/** Whether the club covers the squad outright. */
export const isFullyFromClub = (solution) => clubCoversAll(solution.ownedCount, solutionEntries(solution).length);
export const estimatedMinutes = (solution) => estimatedBuildMinutes(solution.transferCount);
export const streamlineSolutionFacts = (solution) => {
    const facts = solutionFacts({
        totalValue: solution.totalValue,
        transferCost: solution.transferCost,
        ownedCount: solution.ownedCount,
        playerCount: solutionEntries(solution).length,
        transferCount: solution.transferCount,
    });
    /* The solver's own execution estimate wins over the transfer-count guess. */
    return solution.timeS && solution.timeS > 0
        ? {
            ...facts,
            estimatedMinutes: Math.max(1, Math.round(solution.timeS / 60)),
        }
        : facts;
};
export const streamlineTotals = (solution, targetPoints, marks, 
/** Points the user had before opening the screen; the rows cover the rest. */
earnedBefore = 0) => {
    const earned = earnedBefore + pointsEarned(solution, marks);
    return {
        ...streamlineSolutionFacts(solution),
        pointsEarned: earned,
        targetPoints,
        progress: progressRatio(earned, targetPoints),
        costRemaining: costRemaining(solution, marks),
        costSpent: costSpent(solution, marks),
        clubSaving: clubSaving(solution),
        /* Every row completed, or the points alone reach the target: a user who
           types a figure past the target is done, whatever the rows say. */
        isDone: isSolutionDone(solution, marks) ||
            (targetPoints > 0 && earned >= targetPoints),
    };
};
/** Every strategy the solver ships, in the order their tags are shown. */
export const STREAMLINE_TAGS = [
    "clubOnly",
    "fastest",
    "lmv",
    "cheapest",
];
/** The solver lists tags in its own run order; the screen shows them in `STREAMLINE_TAGS` order. */
export const orderStreamlineTags = (tags) => STREAMLINE_TAGS.filter((tag) => tags.includes(tag));
/* A strategy the solver found nothing for (Club Only, when the club is short)
   is left out of the response; the list still shows it, greyed. */
export const missingStreamlineTags = (solutions) => STREAMLINE_TAGS.filter((tag) => !solutions.some((s) => s.tags.includes(tag)));
//# sourceMappingURL=totals.js.map